// @refresh reset
import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IProcessObject, IProcessSectionObject } from 'types'
import type { SectionPatch } from './types'
import { useProject } from 'hooks/useProject'
import api from 'helpers/api'
import { NO_PERMISSION } from 'routes'
import { PERMISSIONS } from 'helpers/auth'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { useApplicationStore } from 'hooks/application'
import { findSection } from 'components/document/helpers/functions'
import { subscribeResourceChanges } from 'helpers/resourceChanges'
import { DataContext } from './DataContext'
import { isNewDocumentState } from 'components/document/helpers/newDocument'
export { useDataContext } from './useDataContext'
export { useOptionalDataContext } from './useOptionalDataContext'
export type { DataContextProps, IExpanded, SectionPatch } from './types'

interface DataContextProviderProps {
  processPublicId: string
  permissionCap?: number
  templateRender?: boolean
  children?: React.ReactNode
}

// With no cap the document's permission level must reach `required`; with a cap, the cap must match it exactly
const hasAccess = (
  permissionLevel: number | undefined,
  permissionCap: number | undefined,
  required: number
): boolean => (permissionCap === undefined ? (permissionLevel ?? 0) >= required : permissionCap === required)

export const DataContextProvider = (props: DataContextProviderProps) => {
  'use no memo'
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [processObject, setProcessObject] = useState<IProcessObject | null>(null)
  const [errorStatusCode, setErrorStatusCode] = useState<number | null>(null)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [focusEditorId, setFocusEditorId] = useState<string | null>(null)
  const [navigationMenu, setNavigationMenu] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [sectionVisibility, setSectionVisibilityState] = useState<Record<string, boolean>>({})
  const [sectionExpanded, setSectionExpandedState] = useState<Record<string, boolean>>({})

  // updateProcess's own debounce; section saves are debounced per section below.
  const updateTimeout = useRef<TimeoutID | null>(null)
  // One debounced save per section, so editing two sections within the debounce saves both. `pending`
  // is what the save sends, taken when the edit is applied: by the time the timer fires the user may
  // have switched documents and the state no longer holds the section.
  const sectionSaves = useRef(
    new Map<
      string,
      { timeout: TimeoutID | null; pending: { processId: string; section: IProcessSectionObject } | null }
    >()
  )
  // Bumped on every document load, so a slow response from an earlier load is ignored — even when the
  // user has since come back to the same document
  const latestLoad = useRef(0)

  const getLoadId = (): number => latestLoad.current

  const isAdmin = hasAccess(processObject?.permissionLevel, props.permissionCap, PERMISSIONS.owner)
  const isContributor = hasAccess(processObject?.permissionLevel, props.permissionCap, PERMISSIONS.contributor)

  useEffect(() => {
    const processId = props.processPublicId
    if (processId && (!processObject || processObject.publicId !== processId)) {
      const load = ++latestLoad.current
      window.scrollTo(0, 0)
      setProcessObject(null)
      setNavigationMenu(false)
      // The provider stays mounted across documents, so don't carry edit mode into the next one
      setEditMode(false)
      setFocusEditorId(null)
      setSectionExpandedState({})
      setSectionVisibilityState({})

      api
        .getProcess(processId, {})
        .then((response) => {
          return response.data
        })
        .then((processData) => {
          api
            .getResourcePermissions('process', processId)
            .then((response) => {
              if (latestLoad.current !== load) return
              if (processData && response.data) {
                processData['permissions'] = response.data
                processData['templateRender'] = props.templateRender ?? false
                document.title = `${processData.name}`
                setProcessObject(processData)
                if (
                  hasAccess(processData.permissionLevel, props.permissionCap, PERMISSIONS.owner) &&
                  isNewDocumentState(location.state)
                ) {
                  // Just created: open in edit mode with the description focused, then drop the
                  // state so a refresh doesn't re-trigger it.
                  setEditMode(true)
                  setFocusEditorId(processId)
                  navigate(location, { replace: true, state: null })
                }
              }
            })
            .catch((error) => {
              if (latestLoad.current !== load) return
              displayErrorMessage(error)
            })
        })
        .catch((error) => {
          if (latestLoad.current !== load) return
          if (error.rawCode && error.rawCode === 404) {
            setErrorStatusCode(error.rawCode)
          } else if (error.rawCode && error.rawCode === 403) {
            // Always navigate to the no-permission view on 403 — without this,
            // a 403 outside a loaded hub context (e.g. template/standalone
            // document renders) would leave the document stuck on the spinner.
            const hubParam = project && project.publicId !== '' ? `hub=${project.publicId}&` : ''
            navigate({
              pathname: NO_PERMISSION,
              search: `?${hubParam}type=document&id=${processId}`
            })
          } else {
            displayErrorMessage(error)
          }
        })
    }
  }, [props.processPublicId, project.publicId])

  // A rename or archive from the sidebar, of this document, shows here at once. It has been saved
  // already, so this sends nothing.
  useEffect(
    () =>
      subscribeResourceChanges('document', props.processPublicId, (change) => {
        if (change.change === 'renamed') document.title = change.name
        setProcessObject((prev) => {
          if (!prev || prev.publicId !== change.id) return prev
          if (change.change === 'renamed') return { ...prev, name: change.name }
          return { ...prev, isDeleted: true, deletedAt: new Date().toISOString() }
        })
      }),
    [props.processPublicId]
  )

  const setSectionExpanded = (sectionId: string, expanded: boolean) => {
    setSectionExpandedState((prev) => {
      if (prev[sectionId] === expanded) return prev
      return { ...prev, [sectionId]: expanded }
    })
  }

  const getAllSectionIds = (children: IProcessSectionObject[]): string[] => {
    const ids: string[] = []
    const collect = (sections: IProcessSectionObject[]) => {
      sections.forEach((section) => {
        ids.push(section.publicId)
        collect(section.children)
      })
    }
    collect(children)
    return ids
  }

  const expandAllSections = () => {
    if (processObject) {
      const allIds = getAllSectionIds(processObject.children)
      const expanded: Record<string, boolean> = {}
      allIds.forEach((id) => (expanded[id] = true))
      setSectionExpandedState(expanded)
    }
  }

  const collapseAllSections = () => {
    if (processObject) {
      const allIds = getAllSectionIds(processObject.children)
      const collapsed: Record<string, boolean> = {}
      allIds.forEach((id) => (collapsed[id] = false))
      setSectionExpandedState(collapsed)
    }
  }

  const updateProcess = (newProcess: IProcessObject, blockApiCall?: boolean) => {
    const existingProcess = processObject
    setProcessObject(newProcess)
    if (updateTimeout.current !== null) cancelTimeout(updateTimeout.current)
    updateTimeout.current = requestTimeout(() => {
      if (!blockApiCall) {
        api
          .updateProcess({
            variables: newProcess.variables,
            name: newProcess.name,
            description: newProcess.description,
            processId: newProcess.publicId,
            logo: newProcess.logo,
            bannerHeight: newProcess.bannerHeight,
            context: { projectId: project.publicId },
            expandByDefault: newProcess.expandByDefault,
            allowComments: newProcess.allowComments,
            isTemplate: newProcess.isTemplate,
            lockedTemplate: newProcess.lockedTemplate
          })
          .catch((error) => {
            displayErrorMessage(error)
            setProcessObject(existingProcess)
          })
      }
    }, 1000)
  }

  const updateProcessSection = (newProcessSection: IProcessSectionObject, children: IProcessSectionObject[]) => {
    children.forEach((child, index) => {
      if (child.publicId === newProcessSection?.publicId) {
        children[index] = newProcessSection
        return children
      } else {
        return updateProcessSection(newProcessSection, child.children)
      }
    })
    return children
  }

  const saveSection = (processId: string, section: IProcessSectionObject) => {
    api
      .updateProcessSection({
        name: section.name,
        description: section.description,
        processId,
        processSectionId: section.publicId,
        pdfIncludeDescription: section.pdfIncludeDescription,
        pdfIncludeSection: section.pdfIncludeSection,
        pageBreakBefore: section.pageBreakBefore,
        context: { projectId: project.publicId }
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  const sectionSaveEntry = (sectionId: string) => {
    let entry = sectionSaves.current.get(sectionId)
    if (!entry) {
      entry = { timeout: null, pending: null }
      sectionSaves.current.set(sectionId, entry)
    }
    return entry
  }

  const queueSectionSave = (sectionId: string) => {
    const entry = sectionSaveEntry(sectionId)
    if (entry.timeout !== null) cancelTimeout(entry.timeout)
    entry.timeout = requestTimeout(() => {
      sectionSaves.current.delete(sectionId)
      if (entry.pending) saveSection(entry.pending.processId, entry.pending.section)
    }, 1000)
  }

  const setSection = (newProcessSection: IProcessSectionObject, blockApiCall?: boolean) => {
    if (processObject) {
      setProcessObject({
        ...processObject,
        children: updateProcessSection(newProcessSection, processObject.children)
      })
      if (!blockApiCall) {
        sectionSaveEntry(newProcessSection.publicId).pending = {
          processId: processObject.publicId,
          section: newProcessSection
        }
        queueSectionSave(newProcessSection.publicId)
      }
    }
  }

  const patchSections = (
    children: IProcessSectionObject[],
    sectionId: string,
    patch: SectionPatch
  ): IProcessSectionObject[] =>
    children.map((child) => {
      if (child.publicId === sectionId) {
        return { ...child, ...(typeof patch === 'function' ? patch(child) : patch) }
      }
      if (!child.children?.length) return child
      return { ...child, children: patchSections(child.children, sectionId, patch) }
    })

  // Unlike setSection, merges into the latest state rather than this render's, so two patches in
  // one tick (an embed's new submission, then the editor's text) both survive.
  const patchSection = (sectionId: string, patch: SectionPatch, blockApiCall?: boolean) => {
    setProcessObject((prev) => {
      if (!prev) return prev
      const next = { ...prev, children: patchSections(prev.children, sectionId, patch) }
      const section = findSection(sectionId, next).section
      if (section && !blockApiCall) sectionSaveEntry(sectionId).pending = { processId: next.publicId, section }
      return next
    })
    if (!blockApiCall) queueSectionSave(sectionId)
  }

  const setSectionVisibility = (sectionId: string, visible: boolean) => {
    setSectionVisibilityState((prev) => {
      if (prev[sectionId] === visible) return prev
      return { ...prev, [sectionId]: visible }
    })
  }

  return (
    <DataContext.Provider
      value={{
        process: processObject,
        setProcess: setProcessObject,
        updateProcess,
        setSection,
        patchSection,
        sectionVisibility,
        setSectionVisibility,
        sectionExpanded,
        setSectionExpanded,
        expandAllSections,
        collapseAllSections,
        isAdmin,
        isContributor,
        permissionCap: props.permissionCap,
        editMode,
        setEditMode,
        focusEditorId,
        setFocusEditorId,
        getLoadId,
        navigationMenu,
        setNavigationMenu,
        searchTerm,
        setSearchTerm,
        errorStatusCode
      }}
    >
      {props.children}
    </DataContext.Provider>
  )
}
